<template>
  <div>
    <input
      :id="inputId"
      :name="name"
      type="checkbox"
      :checked="shouldBeChecked"
      :value="value"
      :class="checkboxClasses"
      :disabled="isDisabled"
      :tabindex="tabIndex"
      @change="updateInput"
      @click="removeMouseFocus"
    />
    <label :class="checkboxLabelClasses" :for="inputId" :aria-label="ariaLabel">
      {{ labelName }}
      <span v-if="formattedLabelText" v-html="formattedLabelText"></span>
    </label>
    <div v-if="helperText" :id="ariaDescribedByName" class="mt-4 flex relative" :class="helperClasses">
      <ErrorIcon v-if="hasError" class="mr-4 mt-4" />
      <p class="text-field-helper">{{ helperText }}</p>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import helperStatus from '../../helpers/status';
import ErrorIcon from '../../icons/ErrorIcon.vue';

export default Vue.extend({
  name: 'Checkbox',
  components: {
    ErrorIcon,
  },
  model: {
    prop: 'modelValue',
    event: 'change',
  },
  props: {
    value: {
      type: [String, Number],
    },
    modelValue: {
      default: false,
    },
    labelName: {
      type: String,
      required: true,
    },
    formattedLabelText: {
      type: String,
    },
    trueValue: {
      default: true,
    },
    falseValue: {
      default: false,
    },
    isDisabled: {
      default: false,
    },
    checkboxId: {
      type: String,
    },
    labelClass: {
      type: String,
      default: 'mb-24',
    },
    status: {
      type: Number,
      default: helperStatus.Neutral,
    },
    helperText: {
      type: String,
      required: false,
    },
    name: {
      type: String,
      default: '',
      required: false,
    },
    ariaLabel: {
      type: String,
      required: false,
    },
    tabIndex: {
      type: Number,
      required: false,
    },
  },
  computed: {
    inputId() {
      return this.checkboxId ? this.checkboxId : `checkbox-${this.value}`;
    },
    shouldBeChecked() {
      if (this.modelValue instanceof Array) {
        return this.modelValue.includes(this.value);
      }
      // Note that `true-value` and `false-value` are camelCase in the JS
      return this.modelValue === this.trueValue;
    },
    checkboxLabelClasses() {
      // disabled functionality for label is controlled
      // by setting the checkbox class to checkbox-disable
      return `checkbox-label ${this.labelClass}`;
    },
    checkboxClasses() {
      return this.isDisabled ? 'checkbox-disabled' : 'checkbox';
    },
    helperClasses() {
      return this.hasError ? 'text-field-error' : '';
    },
    hasError() {
      return this.status === helperStatus.Error;
    },
    ariaDescribedByName() {
      if (!this.helperText) {
        return null;
      }
      return `${this.inputId}-helper`;
    },
  },
  methods: {
    removeMouseFocus(event) {
      // Only remove focus when using mouse
      // screenX and screenY value is 0 for Spacebar
      if (event.screenX + event.screenY !== 0) {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    },
    updateInput(event) {
      const isChecked = event.target.checked;

      if (this.modelValue instanceof Array) {
        const newValue = [...this.modelValue];

        if (isChecked) {
          newValue.push(this.value);
        } else {
          newValue.splice(newValue.indexOf(this.value), 1);
        }

        this.$emit('change', newValue);
      } else {
        this.$emit('change', isChecked ? this.trueValue : this.falseValue);
      }
    },
  },
});
</script>
